import { getEffectiveTrackingQueryParams } from "../trackingPersistence/trackingPersistence";

/**
 * `utm_medium` values that mean a visit arrived from a paid ad.
 *
 * Deliberately identical to the classifier the Ageless funnel reporting already uses (Metabase card
 * 4231, "Provider Finder — Traffic Sources"), so a product decision keyed on "this is paid traffic"
 * and the dashboard reporting on it can never disagree. In production today only `cpc` occurs —
 * every Meta ad set lands as `utm_source=facebook&utm_medium=cpc` — and the rest of the set is here
 * so a new ad platform tagged conventionally is recognised on arrival rather than after a deploy.
 *
 * Organic social carries `utm_medium=organic_social_ageless` and is deliberately absent: it is
 * tagged traffic, but it is not bought.
 */
export const PAID_UTM_MEDIUMS: ReadonlySet<string> = new Set<string>([
  "cpc",
  "ppc",
  "paid",
  "paid_social",
  "paidsocial",
  "display",
]);

/** True when this `utm_medium` names a paid ad placement. */
export function isPaidAdMedium(utmMedium: string | null | undefined): boolean {
  if (!utmMedium) {
    return false;
  }

  return PAID_UTM_MEDIUMS.has(utmMedium.trim().toLowerCase());
}

/**
 * True when the current visit arrived on a paid ad.
 *
 * Reads the effective tracking params rather than `window.location` directly, so the answer
 * survives a client-side navigation within the visit exactly as campaign stamping does. Returns
 * false on the server, where there is no URL to read — call it from an effect, after mount.
 */
export function isPaidAdArrival(): boolean {
  return isPaidAdMedium(getEffectiveTrackingQueryParams().get("utm_medium"));
}
